type TurnstileOptions = {
  sitekey: string;
  action: string;
  callback: (token: string) => void;
  'error-callback'?: (errorCode?: string) => void;
  'expired-callback'?: () => void;
  execution?: 'render' | 'execute';
  appearance?: 'always' | 'execute' | 'interaction-only';
  retry?: 'auto' | 'never';
  'retry-interval'?: number;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  execute: (widgetId: string) => void;
  remove?: (widgetId: string) => void;
};

declare global {
  interface Window { turnstile?: TurnstileApi }
}

let siteKey = '';
let scriptPromise: Promise<TurnstileApi> | null = null;

export function setTurnstileSiteKey(value: string) {
  siteKey = value.trim();
}

export function protectedTurnstileAction(path: string, method = 'GET') {
  if (method.toUpperCase() !== 'POST') return null;
  if (path === '/api/projects') return 'story-create';
  if (/\/continue$/.test(path)) return 'story-continue';
  if (/\/rewrite$/.test(path)) return 'scene-rewrite';
  if (/\/scenes\/\d+\/image$/.test(path)) return 'scene-image';
  if (/\/scenes\/\d+\/audio$/.test(path)) return 'scene-audio';
  if (/\/images$/.test(path)) return 'project-images';
  return null;
}

export function turnstileFailureMessage(hostname = typeof window === 'undefined' ? '' : window.location.hostname, errorCode = '') {
  const currentHost = hostname || '当前访问域名';
  const code = errorCode ? `（${errorCode}）` : '';
  return `安全验证加载失败${code}。请确认 Cloudflare Turnstile 小组件的“允许的主机名”已加入 ${currentHost}；如果已加入，请关闭代理/VPN 或更换网络后重试。`;
}

export function isTurnstileNonRetryableError(errorCode = '') {
  return ['110100', '110110', '110200', '200100'].includes(errorCode);
}

function loadScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  const promise = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-storyweave-turnstile]');
    if (existing) {
      const timer = window.setInterval(() => {
        if (window.turnstile) { window.clearInterval(timer); resolve(window.turnstile); }
      }, 50);
      window.setTimeout(() => { window.clearInterval(timer); reject(new Error('验证码脚本加载超时')); }, 10000);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.storyweaveTurnstile = 'true';
    script.onload = () => window.turnstile ? resolve(window.turnstile) : reject(new Error('验证码脚本未就绪'));
    script.onerror = () => reject(new Error('验证码脚本加载失败，请检查网络后重试。'));
    document.head.appendChild(script);
  });
  scriptPromise = promise.catch((error) => {
    scriptPromise = null;
    throw error;
  });
  return scriptPromise;
}

export async function getTurnstileToken(action: string, mount?: HTMLElement) {
  if (!siteKey) return '';
  const api = await loadScript();
  const ownedContainer = !mount;
  const container = mount || document.createElement('div');
  if (ownedContainer) {
    container.setAttribute('aria-hidden', 'true');
    container.style.cssText = 'position:fixed;left:-10000px;top:-10000px;width:1px;height:1px;overflow:hidden;';
    document.body.appendChild(container);
  } else {
    container.replaceChildren();
  }
  return new Promise<string>((resolve, reject) => {
    let widgetId = '';
    let settled = false;
    const cleanup = () => {
      if (widgetId && api.remove) api.remove(widgetId);
      if (ownedContainer) container.remove();
      else container.replaceChildren();
      window.clearTimeout(timeout);
    };
    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    let lastErrorCode = '';
    const timeout = window.setTimeout(() => fail(new Error(turnstileFailureMessage(undefined, lastErrorCode))), 25000);
    try {
      widgetId = api.render(container, {
        sitekey: siteKey,
        action,
        execution: 'execute',
        appearance: ownedContainer ? 'interaction-only' : 'always',
        retry: 'auto',
        'retry-interval': 3000,
        callback: (token) => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(token);
        },
        'error-callback': (errorCode) => {
          lastErrorCode = errorCode || '';
          if (isTurnstileNonRetryableError(lastErrorCode)) {
            fail(new Error(turnstileFailureMessage(undefined, lastErrorCode)));
            return true;
          }
          // 保留 widget，让 Turnstile 按 auto 策略重试；不能在第一次子请求失败时 remove。
          return false;
        },
        'expired-callback': () => fail(new Error('安全验证已过期，请重新发送验证码。')),
      });
      api.execute(widgetId);
    } catch {
      fail(new Error(turnstileFailureMessage()));
    }
  });
}

export {};
